import type { GuidanceTreeNode } from '../personalLayer/types';

export interface GuidanceNetworkNode {
  id: string;
  label: string;
  critical: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GuidanceNetworkEdge {
  id: string;
  from: string;
  to: string;
  critical: boolean;
}

const NODE_W = 148;
const NODE_H = 44;
const COL_GAP = 72;
const ROW_GAP = 16;
const PAD = 24;

function collectActivities(root: GuidanceTreeNode): GuidanceTreeNode[] {
  const out: GuidanceTreeNode[] = [];
  const walk = (node: GuidanceTreeNode) => {
    if (node.type === 'activity') out.push(node);
    (node.children ?? []).forEach(walk);
  };
  walk(root);
  return out;
}

function activityRanks(activities: GuidanceTreeNode[]): Map<string, number> {
  const byId = Object.fromEntries(activities.map((a) => [a.id, a]));
  const rank = new Map<string, number>();
  activities.forEach((a) => rank.set(a.id, 0));

  for (let iter = 0; iter < activities.length; iter += 1) {
    activities.forEach((a) => {
      const pred = a.dep ? byId[a.dep] : undefined;
      if (pred) {
        rank.set(a.id, Math.max(rank.get(a.id) ?? 0, (rank.get(pred.id) ?? 0) + 1));
      }
    });
  }
  return rank;
}

export function layoutGuidanceNetwork(root: GuidanceTreeNode): {
  nodes: GuidanceNetworkNode[];
  edges: GuidanceNetworkEdge[];
  width: number;
  height: number;
} {
  const activities = collectActivities(root);
  if (activities.length === 0) {
    return { nodes: [], edges: [], width: 320, height: 120 };
  }

  const ranks = activityRanks(activities);
  const maxRank = Math.max(...Array.from(ranks.values()));
  const columns: GuidanceTreeNode[][] = [];
  for (let r = 0; r <= maxRank; r += 1) {
    columns.push(activities.filter((a) => (ranks.get(a.id) ?? 0) === r));
  }

  const nodes: GuidanceNetworkNode[] = [];
  columns.forEach((col, rankIndex) => {
    col.forEach((activity, rowIndex) => {
      nodes.push({
        id: activity.id,
        label: activity.label,
        critical: Boolean(activity.critical),
        x: PAD + rankIndex * (NODE_W + COL_GAP),
        y: PAD + rowIndex * (NODE_H + ROW_GAP),
        w: NODE_W,
        h: NODE_H,
      });
    });
  });

  const posById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const edges: GuidanceNetworkEdge[] = activities
    .filter((a) => a.dep && posById[a.dep] && posById[a.id])
    .map((a) => ({
      id: `net-${a.dep}-${a.id}`,
      from: a.dep!,
      to: a.id,
      critical: Boolean(a.critical && byIdCritical(a, activities)),
    }));

  const width = PAD * 2 + (maxRank + 1) * NODE_W + maxRank * COL_GAP;
  const maxCol = Math.max(...columns.map((c) => c.length), 1);
  const height = PAD * 2 + maxCol * NODE_H + (maxCol - 1) * ROW_GAP;

  return { nodes, edges, width, height };
}

function byIdCritical(activity: GuidanceTreeNode, activities: GuidanceTreeNode[]): boolean {
  const pred = activities.find((a) => a.id === activity.dep);
  return Boolean(activity.critical || pred?.critical);
}

export function networkEdgePath(
  from: GuidanceNetworkNode,
  to: GuidanceNetworkNode
): string {
  const x1 = from.x + from.w;
  const y1 = from.y + from.h / 2;
  const x2 = to.x;
  const y2 = to.y + to.h / 2;
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}
